import React from "react";
import { Link } from "react-router-dom";
import { API } from "../../../../common/Constants";
import { getAPIHeaders } from "../../../../common/Utils";
import { all_routes } from "../../../router/all_routes";
import PreLoader from "../../../../core/common/preLoader";
import useFetch from "../../../../hooks/useFetch";
import CustomToast from "../../../../core/common/customToast";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import CommonTabMenu from "../commonTabMenu";
import SettingsMenu from "./setting-menu";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";

interface ContactDetailsForm {
    // Learning Center (Branding) fields
    institution_name: string;
    institution_logo: string;
    institution_website: string;
    
    // Address fields
    address_line_1: string;
    address_line_2: string;
    street: string;
    city: string;
    state: string;
    zip_code: string;
    country: string;
    phone_number: string;
    whatsapp_phone_number: string;
    email: string;
}

const LearningCenter = () => {
    const routes = all_routes;
    const { isLoading, callFetch } = useFetch();
    const [toastSettings, setToastSettings] = React.useState<{
        show: boolean;
        message: string;
        heading: string;
        type: "ERROR" | "SUCCESS" | "INFO" | "";
    }>({
        show: false,
        message: "",
        heading: "",
        type: "ERROR",
    });

    const [contactDetails, setContactDetails] = React.useState<ContactDetailsForm>({
        institution_name: "",
        institution_logo: "",
        institution_website: "",
        address_line_1: "",
        address_line_2: "",
        street: "",
        city: "",
        state: "",
        zip_code: "",
        country: "",
        phone_number: "",
        whatsapp_phone_number: "",
        email: ""
    });

    const onToastClose = () => {
        setToastSettings({
            show: false,
            message: "",
            heading: "",
            type: "",
        });
    };

    const fetchContactDetails = React.useCallback(() => {
        callFetch(API.SETTINGS_ALL,
            'GET',
            getAPIHeaders(),
            null,
            null
        ).then((res) => {
            // Extract branding settings
            const brandingSettings = res?.tenantSettings?.settingGroups?.find((group: any) => group.category === 'BRANDING')?.settings || [];
            const institutionName = brandingSettings.find((setting: any) => setting.settingKey === 'institution_name')?.currentValue || '';
            const institutionLogo = brandingSettings.find((setting: any) => setting.settingKey === 'institution_logo')?.currentValue || '';
            const institutionWebsite = brandingSettings.find((setting: any) => setting.settingKey === 'institution_website')?.currentValue || '';

            // Extract address settings
            const addressSettings = res?.tenantSettings?.settingGroups?.find((group: any) => group.category === 'ADDRESS')?.settings || [];
            const addressLine1 = addressSettings.find((setting: any) => setting.settingKey === 'address_line_1')?.currentValue || '';
            const addressLine2 = addressSettings.find((setting: any) => setting.settingKey === 'address_line_2')?.currentValue || '';
            const street = addressSettings.find((setting: any) => setting.settingKey === 'street')?.currentValue || '';
            const city = addressSettings.find((setting: any) => setting.settingKey === 'city')?.currentValue || '';
            const state = addressSettings.find((setting: any) => setting.settingKey === 'state')?.currentValue || '';
            const zipCode = addressSettings.find((setting: any) => setting.settingKey === 'zip_code')?.currentValue || '';
            const country = addressSettings.find((setting: any) => setting.settingKey === 'country')?.currentValue || '';
            const phoneNumber = addressSettings.find((setting: any) => setting.settingKey === 'phone_number')?.currentValue || '';
            const whatsappPhoneNumber = addressSettings.find((setting: any) => setting.settingKey === 'whatsapp_phone_number')?.currentValue || '';
            const email = addressSettings.find((setting: any) => setting.settingKey === 'email')?.currentValue || '';

            setContactDetails({
                institution_name: institutionName,
                institution_logo: institutionLogo,
                institution_website: institutionWebsite,
                address_line_1: addressLine1,
                address_line_2: addressLine2,
                street: street,
                city: city,
                state: state,
                zip_code: zipCode,
                country: country,
                phone_number: phoneNumber,
                whatsapp_phone_number: whatsappPhoneNumber,
                email: email
            });
        }).catch((err) => {
            setToastSettings({
                show: true,
                message: "Failed to fetch learning center details.",
                heading: "Error",
                type: "ERROR",
            });
        });
    }, []);

    React.useEffect(() => {
        fetchContactDetails();
    }, [fetchContactDetails]);

    const handleInputChange = (field: keyof ContactDetailsForm, value: string) => {
        setContactDetails(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = (): boolean => {
        // Check all required fields
        const requiredFields = [
            { key: 'institution_name', label: 'Institution Name' },
            { key: 'institution_logo', label: 'Institution Logo URL' },
            { key: 'address_line_1', label: 'Address Line 1' },
            { key: 'street', label: 'Street/Society/Locality' },
            { key: 'city', label: 'City' },
            { key: 'state', label: 'State' },
            { key: 'zip_code', label: 'Zip Code' },
            { key: 'country', label: 'Country' },
            { key: 'phone_number', label: 'Phone Number' },
            { key: 'whatsapp_phone_number', label: 'WhatsApp Number' },
            { key: 'email', label: 'Email Address' }
        ];

        for (const field of requiredFields) {
            if (!contactDetails[field.key as keyof ContactDetailsForm]?.trim()) {
                setToastSettings({
                    show: true,
                    message: `${field.label} is required.`,
                    heading: "Validation Error",
                    type: "ERROR",
                });
                return false;
            }
        }

        // Email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactDetails.email)) {
            setToastSettings({
                show: true,
                message: "Please enter a valid email address.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        // Phone number validation (exactly 10 digits)
        const phoneDigits = contactDetails.phone_number.replace(/[^\d]/g, '');
        if (phoneDigits.length !== 10) {
            setToastSettings({
                show: true,
                message: "Phone number must be exactly 10 digits.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        // WhatsApp phone number validation (exactly 10 digits)
        const whatsappDigits = contactDetails.whatsapp_phone_number.replace(/[^\d]/g, '');
        if (whatsappDigits.length !== 10) {
            setToastSettings({
                show: true,
                message: "WhatsApp number must be exactly 10 digits.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        // URL validation for logo (required)
        try {
            new URL(contactDetails.institution_logo);
        } catch {
            setToastSettings({
                show: true,
                message: "Please enter a valid logo URL.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        // URL validation for website (optional)
        if (contactDetails.institution_website.trim()) {
            try {
                new URL(contactDetails.institution_website);
            } catch {
                setToastSettings({
                    show: true,
                    message: "Please enter a valid website URL.",
                    heading: "Validation Error",
                    type: "ERROR",
                });
                return false;
            }
        }

        return true;
    };

    const saveContactDetails = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        const payload = [
            {
                settingKey: "institution_name",
                scope: "TENANT",
                category: "BRANDING",
                type: "STRING",
                value: contactDetails.institution_name
            },
            {
                settingKey: "institution_logo",
                scope: "TENANT",
                category: "BRANDING",
                type: "STRING",
                value: contactDetails.institution_logo
            },
            {
                settingKey: "institution_website",
                scope: "TENANT",
                category: "BRANDING",
                type: "STRING",
                value: contactDetails.institution_website
            },
            {
                settingKey: "address_line_1",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.address_line_1
            },
            {
                settingKey: "address_line_2",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.address_line_2
            },
            {
                settingKey: "street",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.street
            },
            {
                settingKey: "city",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.city
            },
            {
                settingKey: "state",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.state
            },
            {
                settingKey: "zip_code",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.zip_code
            },
            {
                settingKey: "country",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.country
            },
            {
                settingKey: "phone_number",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.phone_number
            },
            {
                settingKey: "whatsapp_phone_number",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.whatsapp_phone_number
            },
            {
                settingKey: "email",
                scope: "TENANT",
                category: "ADDRESS",
                type: "STRING",
                value: contactDetails.email
            }
        ];

        callFetch(API.SETTINGS_UPDATE_ALL,
            'PUT',
            getAPIHeaders(),
            null,
            payload
        ).then((res) => {
            setToastSettings({
                show: true,
                message: "Learning center details saved successfully.",
                heading: "Success",
                type: "SUCCESS",
            });
        }).catch((err) => {
            setToastSettings({
                show: true,
                message: "Failed to save learning center details.",
                heading: "Error",
                type: "ERROR",
            });
        });
    };

    const resetForm = () => {
        fetchContactDetails();
    };

    return (
        <div>
            <>
                <CustomToast
                    show={toastSettings?.show}
                    message={toastSettings?.message}
                    heading={toastSettings?.heading}
                    type={toastSettings?.type || "INFO"}
                    onClose={onToastClose} />
                <PreLoader showLoader={isLoading} />
                {/* Page Wrapper */}
                <div className="page-wrapper">
                    <div className="content">
                        {/* Breadcrumb */}
                        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                            <div className="my-auto mb-2">
                                <h2 className="mb-1">Application Settings</h2>
                                <nav>
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <Link to={routes.adminDashboard}>
                                                <i className="ti ti-smart-home" />
                                            </Link>
                                        </li>
                                        <li className="breadcrumb-item">Administration</li>
                                        <li className="breadcrumb-item active" aria-current="page">
                                            Settings
                                        </li>
                                    </ol>
                                </nav>
                            </div>
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                        {/* /Breadcrumb */}
                        <CommonTabMenu activeTab="WebsiteSettings" />
                        <div className="row">
                            <div className="col-xl-3 theiaStickySidebar">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex flex-column list-group settings-list">
                                            <SettingsMenu activeTab="LearningCenter" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-9">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="border-bottom mb-3 pb-3">
                                            <div className="d-flex align-items-center">
                                                <span className="avatar avatar-xl p-2 me-2 bg-light flex-shrink-0">
                                                    <ImageWithBasePath src="assets/img/icons/company-icon-01.svg" alt="Contact Details" />
                                                </span>
                                                <div>
                                                    <h4>Learning Center</h4>
                                                    <p className="text-muted mb-0">Manage your learning center and contact information</p>
                                                </div>
                                            </div>
                                        </div>
                                        <form onSubmit={saveContactDetails}>
                                            {/* Learning Center Section */}
                                            <div className="border rounded p-4 mb-4">
                                                <h5 className="mb-3 text-primary">
                                                    <i className="ti ti-school me-2" />
                                                    Learning Center
                                                </h5>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Institution Name <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter Institution Name"
                                                                value={contactDetails.institution_name}
                                                                onChange={(e) => handleInputChange('institution_name', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Institution Logo URL <span className="text-danger">*</span></label>
                                                            <input
                                                                type="url"
                                                                className="form-control"
                                                                placeholder="https://example.com/logo.png"
                                                                value={contactDetails.institution_logo}
                                                                onChange={(e) => handleInputChange('institution_logo', e.target.value)}
                                                                required
                                                            />
                                                            <div className="mt-2 bg-light-500 rounded p-2">
                                                                <small className="form-text text-muted">Enter Institution logo url. Logo should be in 1:1 ratio.</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Institution Website URL</label>
                                                            <input
                                                                type="url"
                                                                className="form-control"
                                                                placeholder="https://institution.com"
                                                                value={contactDetails.institution_website}
                                                                onChange={(e) => handleInputChange('institution_website', e.target.value)}
                                                            />
                                                            <div className="mt-2 bg-light-500 rounded p-2">
                                                                <small className="form-text text-muted">This becomes the home page for the online subscription portal.</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Address Section */}
                                            <div className="border rounded p-4 mb-4">
                                                <h5 className="mb-3 text-primary">
                                                    <i className="ti ti-map-pin me-2" />
                                                    Address & Contact Information
                                                </h5>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Address Line 1 <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter Address Line 1"
                                                                value={contactDetails.address_line_1}
                                                                onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Address Line 2</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter Address Line 2"
                                                                value={contactDetails.address_line_2}
                                                                onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Street/ Society / Locality <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter Street"
                                                                value={contactDetails.street}
                                                                onChange={(e) => handleInputChange('street', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">City <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter City"
                                                                value={contactDetails.city}
                                                                onChange={(e) => handleInputChange('city', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label">State <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter State"
                                                                value={contactDetails.state}
                                                                onChange={(e) => handleInputChange('state', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label">Zip Code <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter Zip Code"
                                                                value={contactDetails.zip_code}
                                                                onChange={(e) => handleInputChange('zip_code', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label">Country <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter Country"
                                                                value={contactDetails.country}
                                                                onChange={(e) => handleInputChange('country', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                                                            <input
                                                                type="tel"
                                                                className="form-control"
                                                                placeholder="1234567890"
                                                                value={contactDetails.phone_number}
                                                                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                                                                required
                                                            />
                                                            <small className="form-text text-muted">Enter 10-digit phone number</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label">WhatsApp Number <span className="text-danger">*</span></label>
                                                            <input
                                                                type="tel"
                                                                className="form-control"
                                                                placeholder="1234567890"
                                                                value={contactDetails.whatsapp_phone_number}
                                                                onChange={(e) => handleInputChange('whatsapp_phone_number', e.target.value)}
                                                                required
                                                            />
                                                            <small className="form-text text-muted">Enter 10-digit WhatsApp number</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label">Email Address <span className="text-danger">*</span></label>
                                                            <input
                                                                type="email"
                                                                className="form-control"
                                                                placeholder="contact@institution.com"
                                                                value={contactDetails.email}
                                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex align-items-center justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-light border me-3"
                                                    onClick={resetForm}
                                                >
                                                    Reset
                                                </button>
                                                <button type="submit" className="btn btn-primary">
                                                    Save Learning Center Details
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
                        <CopyRightInfo />
                    </div>
                </div>
                {/* /Page Wrapper */}
            </>
        </div>
    );
};

export default LearningCenter;
